import { ParsedQuery } from "query-string";

export type SearchParamPrimitive = string | number | boolean | null | undefined;
export type SearchParamValue = SearchParamPrimitive | Array<SearchParamPrimitive>;

export type PagerParams = {
    per_page: number;
    order?: string;
    direction?: string;
};

type CleanSearchParamsOptions<TParams extends HashMap<SearchParamValue>> = {
    shouldKeep?: (key: keyof TParams & string, value: SearchParamValue) => boolean;
};

const isSearchParamPrimitive = (value: unknown): value is SearchParamPrimitive =>
    value === null || value === undefined || ["string", "number", "boolean"].includes(typeof value);

const isSearchParamValue = (value: unknown): value is SearchParamValue =>
    isSearchParamPrimitive(value) || (Array.isArray(value) && value.every(isSearchParamPrimitive));

export const normalizeSearchParams = <TParams extends HashMap<SearchParamValue>>(
    params: HashMap<unknown>
): TParams =>
    Object.entries(params).reduce<TParams>((acc, [key, value]) => {
        if (isSearchParamValue(value)) {
            acc[key as keyof TParams & string] = value as TParams[keyof TParams & string];
        }

        return acc;
    }, {} as TParams);

export const splitCsv = (value: unknown): string[] => {
    if (Array.isArray(value)) {
        return value.flatMap(splitCsv);
    }

    if (typeof value !== "string") {
        return [];
    }

    return Array.from(new Set(value.split(",").map((item) => item.trim()).filter(Boolean)));
};

export const joinCsv = (values: Array<string | null | undefined>): string | undefined => {
    const csv = Array.from(new Set(values.map((item) => item?.trim()).filter(Boolean))).join(",");

    return csv || undefined;
};

export const cleanSearchParams = <TParams extends HashMap<SearchParamValue>>(
    params: TParams,
    options: CleanSearchParamsOptions<TParams> = {}
): TParams =>
    Object.entries(params).reduce<TParams>((acc, [key, value]) => {
        if (value === null || value === undefined || value === "") {
            return acc;
        }

        if (options.shouldKeep && !options.shouldKeep(key as keyof TParams & string, value)) {
            return acc;
        }

        acc[key as keyof TParams & string] = value as TParams[keyof TParams & string];

        return acc;
    }, {} as TParams);

export const getSingleQueryValue = (value: ParsedQuery[string] | unknown): string => {
    if (Array.isArray(value)) {
        return String(value[0] ?? "");
    }

    return String(value ?? "");
};
